import { Link } from "@tanstack/react-router";
import { PALS } from "@/lib/pals-data";
import { cardLinkProps } from "@/lib/card-link";
import { playstyleLabel, type PlayerProfile } from "@/lib/player-profiles-shared";

/** Resolve a favourite Pal name to its wiki page, when it is a real Pal. */
export function palLink(name: string) {
  if (!name) return null;
  const target = name.trim().toLowerCase();
  return PALS.find((p) => p.name.toLowerCase() === target || p.slug === target) ?? null;
}

/** Compact profile tile used on the /players directory. */
export function PlayerProfileCard({ profile }: { profile: PlayerProfile }) {
  const pal = palLink(profile.favorite_pal);
  return (
    <article className="glass rounded-2xl p-5">
      <div className="flex flex-wrap items-center gap-2">
        <Link
          to="/players/$handle"
          params={{ handle: profile.handle }}
          className="text-lg font-bold hover:text-primary"
        >
          {profile.display_name}
        </Link>
        <span className="font-mono text-xs text-muted-foreground">/players/{profile.handle}</span>
        <span className="rounded-full border border-border/60 px-2 py-0.5 text-[11px]">
          {playstyleLabel(profile.playstyle)}
        </span>
        {profile.trade_ready && (
          <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[11px] font-semibold text-primary">
            Open to trades
          </span>
        )}
      </div>

      {profile.bio && <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{profile.bio}</p>}

      <dl className="mt-3 grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
        <div>
          <dt className="text-muted-foreground">Wants</dt>
          <dd className="font-semibold">{profile.wants}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Spares</dt>
          <dd className="font-semibold">{profile.haves}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Region</dt>
          <dd className="font-semibold">{profile.region || "—"}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Favourite Pal</dt>
          <dd className="font-semibold">
            {pal ? (
              <Link to="/pals/$slug" params={{ slug: pal.slug }} className="text-primary hover:underline">
                {pal.name}
              </Link>
            ) : (
              profile.favorite_pal || "—"
            )}
          </dd>
        </div>
      </dl>

      {profile.favorite_card && (
        <p className="mt-3 text-xs text-muted-foreground">
          Favourite card:{" "}
          <Link {...cardLinkProps(profile.favorite_card)} className="font-mono text-primary hover:underline">
            {profile.favorite_card}
          </Link>
        </p>
      )}
    </article>
  );
}
